import { Signal } from '@verlo/shared/components/ui/signal';
import { Button } from '@verlo/shared/components/ui/button';
import { Sparkles, Table2, Ban } from 'lucide-react';
import { reservationsApi } from '@verlo/shared/services/api';
import { toast } from 'sonner';
import { hasAllergyMention, noteHint } from '../../lib/allergyNotes';
import BookingStatusActions from './BookingStatusActions';
import { QUEUE_STATE } from '../../lib/bookingQueue';
import { sentence } from '../../lib/fohState';

// One booking on the queue: a rail with one word of state, the facts a host needs on one
// line, and the one thing to do next as a button. Tap the card for the rest.
//
// `state` is the derived queue state (lib/bookingQueue.js), not the stored status — that
// is how "late" exists without being written anywhere.

// "35 min late" up to an hour and a half; past that, hours — "10h late" is a booking
// somebody forgot to mark no-show, not a party to wait for.
const lateLabel = (mins) => (mins < 90 ? `${mins} min` : `${Math.round(mins / 60)}h`);

const BookingCard = ({
  r, state, isOpen, onToggle, suggestion, onSuggest, onAcceptSuggestion, onEdit, onChanged, isToday = true,
}) => {
  const st = QUEUE_STATE[state.key] || QUEUE_STATE.upcoming;
  const needsTable = !r.table_id && !r.table_name && !['done', 'no_show', 'cancelled'].includes(state.key);
  const closed = ['done', 'no_show', 'cancelled'].includes(state.key);
  const hasAllergy = hasAllergyMention(r.notes, r.special_requests, r.dietary_requirements, r.allergy_notes);
  const hint = noteHint(r.notes) || noteHint(r.special_requests);
  const sug = suggestion;
  const hasSuggestion = sug && sug !== 'loading' && sug.table_id;
  const dot = <span className="w-[3px] h-[3px] rounded-full bg-ink-faint shrink-0" />;

  const markNoShow = async () => {
    try {
      await reservationsApi.noshow(r.id);
      toast.success(`${r.customer_name} marked no-show`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not mark no-show');
    }
  };

  // The one action for a resting card.
  const primary = () => {
    if (needsTable) {
      return (
        <Button size="sm" className="h-9 px-3.5 text-[13px] font-semibold bg-verlo hover:bg-verlo-hover"
          disabled={sug === 'loading'} onClick={() => onSuggest(r.id)} data-testid={`suggest-${r.id}`}>
          <Sparkles size={13} className="mr-1.5" />
          {sug === 'loading' ? 'Checking…' : 'Find a table'}
        </Button>
      );
    }
    if (state.key === 'late') {
      return (
        <div className="flex gap-1.5">
          <BookingStatusActions reservation={r} onStatusChange={onChanged} variant="primary" />
          <Button size="sm" variant="outline" className="h-9 text-[12.5px] text-signal-stop border-signal-stop-line hover:bg-signal-stop-tint hover:text-signal-stop"
            onClick={markNoShow} data-testid={`noshow-${r.id}`}>
            <Ban size={13} className="mr-1" /> No-show
          </Button>
        </div>
      );
    }
    if (closed) return null;
    // Actions follow the date (FOH UX review, wave 3): Arrived / No-show belong to the door
    // tonight. Planning another day, the card's one action is Edit.
    if (!isToday) {
      return (
        <Button size="sm" variant="outline" className="h-9 text-[12.5px]" onClick={() => onEdit(r)} data-testid={`edit-${r.id}`}>Edit</Button>
      );
    }
    return <BookingStatusActions reservation={r} onStatusChange={onChanged} variant="primary" />;
  };

  return (
    <div
      data-testid={`reservation-card-${r.id}`}
      className={`flex rounded-lg border bg-white overflow-hidden transition-shadow ${
        closed ? 'opacity-60' : ''} ${isOpen ? 'border-ink-faint shadow-lg' : `${st.border} hover:border-ink-faint`}`}
    >
      <div className={`w-1 shrink-0 ${st.rail}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-3 p-3 cursor-pointer" onClick={onToggle}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-[14px] text-ink truncate">{r.customer_name}</span>
              <span className="font-mono font-medium text-[13px] text-ink-soft tabular-nums">· {r.party_size}</span>
              {hasAllergy && (
                <Signal tone="stop">allergy</Signal>
              )}
            </div>
            <div className="mt-0.5 text-[12px] text-ink-soft flex items-center gap-1.5 min-w-0">
              <span className="font-mono tabular-nums shrink-0">{r.time}</span>
              {dot}
              <span className={`font-medium ${st.text}`}>
                {state.key === 'late' ? `${lateLabel(state.minutesLate)} late` : sentence(st.word)}
              </span>
              {r.table_name ? <>{dot}<span className="text-ink-soft shrink-0">{r.table_name}</span></>
                : needsTable && <>{dot}<span className="text-signal-warn shrink-0">No table</span></>}
              {hint && <>{dot}<span className="truncate">{hint}</span></>}
            </div>
          </div>
          {!isOpen && (
            <div className="shrink-0" onClick={(e) => e.stopPropagation()}>{primary()}</div>
          )}
        </div>

        {/* suggestion result — the suggested table resolved into a button */}
        {needsTable && sug && sug !== 'loading' && !isOpen && (
          <div className="px-3 pb-2.5 -mt-1" onClick={(e) => e.stopPropagation()}>
            {sug.table_id ? (
              <div className="flex items-center gap-2 rounded-md bg-surface-2 border border-hairline px-2.5 py-1.5 text-[12px]">
                <span className="font-semibold text-ink shrink-0">{sug.table_name}</span>
                <span className="text-ink-soft truncate">{sug.reason}</span>
                <div className="flex-1" />
                <button className="text-verlo font-semibold shrink-0" onClick={() => onAcceptSuggestion(r.id, sug.table_id)} data-testid={`assign-${r.id}`}>
                  Seat at {sug.table_name}
                </button>
                <span className="text-ink-faint">·</span>
                <button className="text-ink-soft shrink-0" onClick={() => onEdit(r)}>Pick another</button>
              </div>
            ) : (
              <p className="text-[12px] text-ink-soft">
                {sug.reason}. Or <button className="text-verlo font-medium" onClick={() => onEdit(r)}>assign manually</button>
              </p>
            )}
          </div>
        )}

        {isOpen && (
          <div className="border-t border-hairline px-3 py-3 space-y-3" onClick={(e) => e.stopPropagation()}>
            <dl className="grid grid-cols-[auto,1fr,auto] gap-x-3 gap-y-1.5 text-[13px] items-center">
              {r.customer_phone && (
                <>
                  <dt className="text-[10px] uppercase tracking-wide text-ink-faint font-mono">Phone</dt>
                  <dd className="text-ink tabular-nums">{r.customer_phone}</dd>
                  <a href={`tel:${r.customer_phone}`} className="text-[12px] font-semibold text-verlo border border-hairline rounded px-2.5 py-1">Call</a>
                </>
              )}
              {(r.notes || r.special_requests) && (
                <>
                  <dt className="text-[10px] uppercase tracking-wide text-ink-faint font-mono">Notes</dt>
                  <dd className="col-span-2 text-ink-soft">{[r.notes, r.special_requests].filter(Boolean).join(' · ')}</dd>
                </>
              )}
              {r.is_regular && (
                <>
                  <dt className="text-[10px] uppercase tracking-wide text-ink-faint font-mono">Guest</dt>
                  <dd className="col-span-2 text-ink-soft">Regular · {r.visit_count || 0} visits</dd>
                </>
              )}
              <dt className="text-[10px] uppercase tracking-wide text-ink-faint font-mono">Source</dt>
              <dd className="col-span-2 text-ink-soft capitalize">{r.source || 'manual'}</dd>
            </dl>

            <div className="flex flex-wrap gap-2">
              {needsTable && (hasSuggestion ? (
                <Button size="sm" className="h-8 text-[12.5px] bg-verlo hover:bg-verlo-hover" onClick={() => onAcceptSuggestion(r.id, sug.table_id)}>
                  <Table2 size={13} className="mr-1.5" /> Seat at {sug.table_name} · {sug.reason}
                </Button>
              ) : (
                <Button size="sm" variant="outline" className="h-8 text-[12.5px]" disabled={sug === 'loading'} onClick={() => onSuggest(r.id)}>
                  <Sparkles size={13} className="mr-1.5" /> {sug === 'loading' ? 'Checking…' : 'Find a table'}
                </Button>
              ))}
              {needsTable && (
                <Button size="sm" variant="outline" className="h-8 text-[12.5px]" onClick={() => onEdit(r)}>
                  <Table2 size={13} className="mr-1.5" /> Assign manually
                </Button>
              )}
              {!closed && <BookingStatusActions reservation={r} onStatusChange={onChanged} variant="full" />}
              <Button size="sm" variant="outline" className="h-8 text-[12.5px]" onClick={() => onEdit(r)}>Edit</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingCard;
